import { images } from '../images';
import type { Course } from '../../types/content';

const yearRound = ['dubai', 'london'];

export const englishCourses: Course[] = [
  {
    slug: 'general-english',
    name: 'General English',
    group: 'english',
    season: 'year-round',
    keyword: 'general english course',
    metaTitle: 'General English Course | Intensive & Semi-Intensive | ES World',
    metaDescription: 'Build speaking, listening, reading and writing in our General English course in Dubai or London. All levels. Start any Monday.',
    h1: 'General English course for everyday confidence',
    lead: 'Our General English course builds confidence in all four skills — speaking, listening, reading and writing — through lessons connected to everyday life. Choose intensive or semi-intensive classes, start any Monday, and study for 1 to 52 weeks, from beginner to proficient.',
    cardText: 'Build all four skills for work, travel and daily life.',
    audience: 'Adults and students, all levels',
    image: images.classroom,
    facts: [
      { label: 'Course length', value: '1 to 52 weeks' },
      { label: 'Lessons a week', value: 'Dubai 15 or 30 · London 20 or 40' },
      { label: 'Levels', value: 'Beginner to Proficiency' },
      { label: 'Start dates', value: 'Every Monday, all year' },
    ],
    valueTitle: 'Learn English you use every day',
    valueText: 'Lessons follow National Geographic Learning’s Life series with a task-based approach on interactive smart boards. Every topic links to real situations, so you can use new language outside class straight away.',
    outcomes: [
      'Speak with more confidence in everyday conversations',
      'Understand people when they speak at a natural speed',
      'Read and write clearly for daily life and work',
      'Use grammar and vocabulary more accurately',
      'Build a strong base for IELTS, Business or Academic English',
      'Practise outside class on our social programme',
    ],
    whoTitle: 'Made for every level',
    whoText: 'Open to learners from beginner to proficient. Before you start, you take an online placement test — grammar, writing and speaking — in the student portal, and we place you in the right class.',
    whoFor: [
      { title: 'Adults who travel or work', text: 'You want to talk with colleagues, clients or new friends without stress.' },
      { title: 'Future exam students', text: 'You want a strong base before IELTS or an academic course.' },
      { title: 'Newcomers to a new city', text: 'You want English for shopping, banking, renting and meeting people.' },
      { title: 'Returning learners', text: 'You studied English at school and want to use it again.' },
    ],
    weekTitle: 'Your week, step by step',
    weekText: 'Every day has a clear focus. On Friday you review the week, and your teacher gives you feedback on your progress.',
    week: [
      { day: 'Monday', focus: 'New topic, key words and a speaking warm-up' },
      { day: 'Tuesday', focus: 'Grammar in context, with practice in pairs' },
      { day: 'Wednesday', focus: 'Listening and reading with real materials' },
      { day: 'Thursday', focus: 'Writing task and group discussion' },
      { day: 'Friday', focus: 'Review, short quiz and personal feedback' },
    ],
    faqs: [
      { q: 'How long does each level take?', a: 'About 12 weeks on the semi-intensive course or 6 weeks on the intensive course. How long you need overall depends on your starting level and your goal.' },
      { q: 'How do I move up a level?', a: 'When you feel ready, you can retake the level test in the student portal. Your teacher and the academic team then confirm your new level.' },
      { q: 'Can I switch to another course?', a: 'Yes, if you meet the entry level and the change is approved. If you move away from the intensive timetable, the intensive option may not carry over.' },
      { q: 'How is English taught?', a: 'Communicatively. You use new language in pairs, groups and real tasks, supported by smart-board materials — and you keep practising on our social activities.' },
    ],
    ctas: {
      heroPrimary: 'Book a free lesson',
      heroSecondary: 'See a sample week',
      midPrimary: 'Get my course price',
      midLink: { label: 'Add speaking practice', to: '/programmes/speaking-class' },
      bottomPrimary: 'Send my course enquiry',
    },
    locations: yearRound,
  },
  {
    slug: 'general-english-30-plus',
    name: 'General English 30+',
    group: 'english',
    season: 'year-round',
    keyword: 'english courses for adults',
    metaTitle: 'English Courses for Adults 30+ | General English 30+ | ES World',
    metaDescription: 'Join English courses for adults aged 30 and over. Study with classmates at the same stage of life. Start any Monday.',
    h1: 'English courses for adults aged 30 and over',
    lead: 'General English 30+ is one of our English courses for adults who want to study with people at the same stage of life. You learn in a calm, focused class with classmates aged 30 and over. Topics come from work, family, travel and current events, so every lesson feels relevant to you.',
    cardText: 'Study with classmates aged 30 and over.',
    audience: 'Adults, 30+',
    image: images.teacher,
    facts: [
      { label: 'Course length', value: '1 to 24 weeks' },
      { label: 'Lessons', value: '15 or 30 a week' },
      { label: 'Levels', value: 'Elementary to advanced' },
      { label: 'Start dates', value: 'Every Monday' },
    ],
    valueTitle: 'Learn with people like you',
    valueText: 'Your classmates share similar goals and life experience. Lessons move at a steady pace, with time for questions and real discussion.',
    outcomes: [
      'Talk about your job, your family and your interests',
      'Join discussions on news and current topics',
      'Write clear emails for work and personal life',
      'Understand films, podcasts and news reports more easily',
      'Use grammar with more accuracy and less stress',
      'Feel confident when you travel or meet new people',
    ],
    whoTitle: 'Right for adult learners',
    whoText: 'This course suits adults aged 30 and over who want a focused class. You join a group at your level after a short online test.',
    whoFor: [
      { title: 'Working professionals', text: 'You want better English for your career, in a calm group.' },
      { title: 'Career breaks', text: 'You have time between jobs and want to invest in your English.' },
      { title: 'Retired learners', text: 'You want to travel, read and talk with more confidence.' },
      { title: 'Parents', text: 'You want English for family life abroad or for your children\'s school.' },
    ],
    weekTitle: 'A calm, focused week',
    weekText: 'Each day mixes skills practice with discussion. On Friday your teacher reviews your progress with you.',
    week: [
      { day: 'Monday', focus: 'New topic and discussion in small groups' },
      { day: 'Tuesday', focus: 'Grammar for clear and accurate speaking' },
      { day: 'Wednesday', focus: 'Listening to news and podcasts' },
      { day: 'Thursday', focus: 'Writing for work and personal life' },
      { day: 'Friday', focus: 'Review and personal feedback' },
    ],
    faqs: [
      { q: 'Is everyone in the class over 30?', a: 'Yes. This course is only for students aged 30 and over.' },
      { q: 'Can I join the social programme?', a: 'Yes. You can join all activities. Some activities are planned for adult students only.' },
    ],
    ctas: {
      heroPrimary: 'Book a free adult lesson',
      heroSecondary: 'See the adult week',
      midPrimary: 'Get my 30+ course price',
      midLink: { label: 'Compare with General English', to: '/programmes/general-english' },
      bottomPrimary: 'Send my 30+ enquiry',
    },
    locations: yearRound,
  },
  {
    slug: 'speaking-class',
    name: 'Speaking Class',
    group: 'english',
    season: 'year-round',
    keyword: 'spoken english classes',
    metaTitle: 'English Speaking Course | Spoken English Classes | ES World',
    metaDescription: 'Speak English with confidence. Discussions, debates and presentations in every lesson, in Dubai or London. All levels.',
    h1: 'English speaking course to help you speak with confidence',
    lead: 'Our Speaking Class is for learners who read and write better than they speak. Every lesson is built around discussions, debates and presentations, so you cut down your “thinking time” and speak more naturally — at work, while travelling and in daily life.',
    cardText: 'Discussions, debates and presentations in every lesson.',
    audience: 'Adults, 18+',
    image: images.hero,
    facts: [
      { label: 'Course length', value: 'From 6 weeks' },
      { label: 'Lessons a week', value: 'Dubai 15 · London 20' },
      { label: 'Levels', value: 'Beginner to Advanced' },
      { label: 'Start dates', value: 'Dubai every 6 weeks · London every Monday' },
    ],
    valueTitle: 'Speak more in every lesson',
    valueText: 'Most of the lesson is speaking. You work in pairs and small groups, and your teacher gives clear feedback on pronunciation, fluency and accuracy.',
    outcomes: [
      'Start and keep a conversation going with new people',
      'Give your opinion and explain your reasons in a debate',
      'Present ideas clearly in front of a group',
      'Improve your pronunciation and sound more natural',
      'Speak with confidence in professional situations',
      'Think less and respond faster when you speak',
    ],
    whoTitle: 'For learners who want to talk',
    whoText: 'Open to adults aged 18+ at every level, from beginner to advanced. You take a short placement test so we can match you with the right group.',
    whoFor: [
      { title: 'Quiet speakers', text: 'Your reading and writing are stronger than your speaking.' },
      { title: 'Professionals', text: 'You need to speak in calls, meetings and presentations.' },
      { title: 'Exam students', text: 'You want more practice for a speaking test.' },
      { title: 'Travellers', text: 'You want to talk with people on your trips.' },
    ],
    weekTitle: 'Five days of real talk',
    weekText: 'Each day you practise one type of speaking. At the end of the week you give a short talk and get personal feedback.',
    week: [
      { day: 'Monday', focus: 'Introductions and small talk with new partners' },
      { day: 'Tuesday', focus: 'Giving opinions in a group discussion' },
      { day: 'Wednesday', focus: 'Pronunciation practice and role plays' },
      { day: 'Thursday', focus: 'Debate on a topic of the week' },
      { day: 'Friday', focus: 'Short presentation and personal feedback' },
    ],
    faqs: [
      { q: 'Who can join the Speaking Class?', a: 'Adults aged 18 and over from anywhere in the world, at any level from beginner to advanced. Classes are taught by native English-speaking teachers.' },
      { q: 'Will I get a certificate?', a: 'Yes. With at least 80% attendance and 80% in the final exam, you receive a course completion certificate. Otherwise you receive a certificate of attendance.' },
      { q: 'I am a complete beginner. Can I still join?', a: 'Yes. Teachers use visual, interactive, audio and video materials so beginners can take part from the first lesson.' },
      { q: 'Can I add the Speaking Class to General English?', a: 'Yes. Many students combine the two. Ask our team about the best timetable for you.' },
    ],
    ctas: {
      heroPrimary: 'Try a free speaking lesson',
      heroSecondary: 'See the speaking week',
      midPrimary: 'Get my Speaking Class price',
      midLink: { label: 'Read our speaking course guide', to: '/blog/how-to-choose-an-english-speaking-course' },
      bottomPrimary: 'Send my speaking enquiry',
    },
    locations: yearRound,
  },
  {
    slug: 'english-for-ielts',
    name: 'English for IELTS',
    group: 'english',
    season: 'year-round',
    keyword: 'english for ielts course',
    metaTitle: 'English for IELTS | Build Your Level Before IELTS | ES World Dubai',
    metaDescription: 'Not ready for IELTS Preparation yet? English for IELTS builds your level and exam skills with Cambridge Mindset for IELTS. Dubai.',
    h1: 'English for IELTS: build your level before exam prep',
    lead: 'English for IELTS is a 12-week course that raises your English level while you learn how the IELTS test works. Lessons use Cambridge’s Mindset for IELTS and real-life topics, so you build all four skills and get used to exam tasks before you move on to full IELTS Preparation.',
    cardText: 'Raise your level and learn the test format.',
    audience: 'Students and adults',
    image: images.classroom,
    facts: [
      { label: 'Course length', value: '12 weeks per level' },
      { label: 'Timetable', value: 'Semi-intensive' },
      { label: 'Levels', value: 'Pre-intermediate (A2) and Intermediate (B1)' },
      { label: 'Target', value: 'Intermediate level aims for IELTS band 5.5' },
    ],
    valueTitle: 'Get exam-ready step by step',
    valueText: 'Each lesson mixes real-life topics with IELTS-style tasks. Regular mock tests track your progress, and a guide to common mistakes helps you avoid losing marks.',
    outcomes: [
      'Understand every part of the IELTS test',
      'Practise listening, reading, writing and speaking together',
      'Learn vocabulary and grammar for common exam topics',
      'Track your progress with mock tests',
      'Avoid the mistakes that cost most candidates marks',
      'Move on to IELTS Preparation with confidence',
    ],
    whoTitle: 'For learners who need a stronger base',
    whoText: 'Choose the Intermediate course if your placement test shows B1 and you are aiming for band 5.5. If you are below B1, start with the Pre-Intermediate course first.',
    whoFor: [
      { title: 'Future university students', text: 'You will need an IELTS score but are not at exam level yet.' },
      { title: 'Visa and migration applicants', text: 'You need IELTS for an application in the future.' },
      { title: 'General English students', text: 'You want to start focusing on the exam.' },
      { title: 'Retake candidates', text: 'Your last score was lower than you needed.' },
    ],
    weekTitle: 'A week of skills and exam practice',
    weekText: 'Each day focuses on one skill through a real-life topic. On Friday you review your progress with your teacher.',
    week: [
      { day: 'Monday', focus: 'New topic, vocabulary and a listening task' },
      { day: 'Tuesday', focus: 'Reading skills with IELTS-style questions' },
      { day: 'Wednesday', focus: 'Writing practice with teacher feedback' },
      { day: 'Thursday', focus: 'Speaking practice in pairs and groups' },
      { day: 'Friday', focus: 'Mini mock test and progress review' },
    ],
    faqs: [
      { q: 'Which level should I choose?', a: 'If your placement test shows B1 (Intermediate) and you are aiming for band 5.5, choose the Intermediate course. If you are Pre-Intermediate, take the Pre-Intermediate course first.' },
      { q: 'Is the course online?', a: 'No. English for IELTS is taught in person at our Dubai campus.' },
      { q: 'What comes next?', a: 'When you finish, you can move on to our full IELTS Preparation course.' },
    ],
    ctas: {
      heroPrimary: 'Take the free level test',
      heroSecondary: 'See the course week',
      midPrimary: 'Get my course price',
      midLink: { label: 'See IELTS Preparation', to: '/programmes/ielts-preparation' },
      bottomPrimary: 'Send my IELTS enquiry',
    },
    locations: ['dubai'],
  },
  {
    slug: 'ielts-preparation',
    name: 'IELTS Preparation',
    group: 'english',
    season: 'year-round',
    keyword: 'ielts preparation course',
    metaTitle: 'IELTS Preparation Course | Academic IELTS Training | ES World',
    metaDescription: 'Prepare for IELTS with mock exams, writing feedback and test strategies in Dubai or London. Intermediate level and above.',
    h1: 'IELTS preparation course for test day',
    lead: 'Our IELTS Preparation course gets you ready for all four parts of the test — Listening, Reading, Writing and Speaking — with interactive lessons, writing practice, class discussions and full mock exams. The focus is Academic IELTS, with extra work on grammar, vocabulary, pronunciation and study skills.',
    cardText: 'Strategies, mock exams and writing feedback.',
    audience: 'Students and adults',
    image: images.teacher,
    facts: [
      { label: 'Course length', value: '4 to 12 weeks' },
      { label: 'Target', value: 'Band 5.5 to 6.5, with support for 7.0+' },
      { label: 'Levels', value: 'Intermediate (B1) and up' },
      { label: 'Start dates', value: 'Dubai every 6 weeks · London every Monday' },
    ],
    valueTitle: 'Feel ready on test day',
    valueText: 'You learn what each question type asks for and how to manage your time. Mock exams show where you are and what to practise next, and your teacher gives you personal feedback.',
    outcomes: [
      'Know the format of every part of the exam',
      'Manage your time in reading and listening',
      'Plan and write Task 1 and Task 2 answers',
      'Speak clearly in all three parts of the speaking test',
      'Use grammar, vocabulary and pronunciation that score well',
      'Track your progress with mock exams',
    ],
    whoTitle: 'For future IELTS candidates',
    whoText: 'You need an intermediate level or higher. For the best results, join when you are about 0.5 to 1 band below your target score.',
    whoFor: [
      { title: 'University applicants', text: 'Many Master’s programmes ask for around 6.5; top UK universities ask for 7.0 or more.' },
      { title: 'Retake candidates', text: 'You took IELTS before and need a higher score.' },
      { title: 'Teachers in the UAE', text: 'Teacher registration with the Ministry of Education asks for Academic IELTS 7+.' },
      { title: 'Visa and migration', text: 'You need a score for an immigration application.' },
    ],
    weekTitle: 'Practise every part of the test',
    weekText: 'Each day covers one part of the exam. On Friday you take a timed practice test and talk about your results with your teacher.',
    week: [
      { day: 'Monday', focus: 'Listening techniques and timed practice' },
      { day: 'Tuesday', focus: 'Reading techniques for each question type' },
      { day: 'Wednesday', focus: 'Writing Task 1 with teacher feedback' },
      { day: 'Thursday', focus: 'Writing Task 2 and speaking practice' },
      { day: 'Friday', focus: 'Timed mock test and result review' },
    ],
    faqs: [
      { q: 'How long should I prepare?', a: 'Most students prepare for 4 to 8 weeks. In London, the full course runs for 12 weeks. Your placement test and target score help us suggest the right length.' },
      { q: 'How long is an IELTS score valid?', a: 'IELTS results are valid for two years.' },
      { q: 'When do I get my results?', a: 'Usually 3 to 5 days after a computer-based test, or 13 days after a paper-based test.' },
      { q: 'Who accepts IELTS?', a: 'Universities, employers and governments around the world use IELTS for study, work and immigration.' },
    ],
    ctas: {
      heroPrimary: 'Book a free IELTS lesson',
      heroSecondary: 'See the practice week',
      midPrimary: 'Get my exam course price',
      midLink: { label: 'Build your level with English for IELTS', to: '/programmes/english-for-ielts' },
      bottomPrimary: 'Send my exam enquiry',
    },
    locations: yearRound,
  },
];
